<template>
  <div class="subjects text-dark" :class="{'subjects--is-activities': isActivities}">
    <Banner v-if="noActiveSchoolYears"></Banner>
    <div class="subjects-header" v-if="!isActivities">
      <div class="subjects-header__left">
        <h1>{{ i18n[locale]['headline'] }}</h1>
        <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
        <DoFiltersFilter
          :title="i18n[locale]['subjects']"
          :tags="subjectTypeFilters"
          :selectedTags="selectedSubjectTypeFilters"
          @select="toggleSelectedSubjectTypeFilters"
          @clear="clearSelectedSubjectTypeFilters"
        >
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="i18n[locale]['grades']"
          :tags="klassTags"
          :selectedTags="selectedKlassFilters"
          @select="toggleKlassTag"
          @clear="clearSelectedKlassFilters"
        >
        </DoFiltersFilter>
      </div>
      <div class="subjects-header__right">
        <button
          class="do-bttn do-bttn--large"
          @click="setImportSubjects"
          v-if="!isLoading && klassesWithoutSubjects.length > 0 && schoolIsFromDK"
        >
          {{ i18n[locale]['import_subjects'] }}
        </button>
      </div>
    </div>
    <Loader v-if="isLoading" color="blue"></Loader>

    <template v-else>
      <div v-if="klassIds.length < 1">
        <h2 class="text-explainer">{{ i18n[locale]['create_classes_first'] }}</h2>
      </div>

      <div v-else class="subjects-table" :style="{'width': tableWidth}">
        <table class="d-table">
          <thead ref="tableHead">
            <tr>
              <th style="min-width: 160px;"></th>
              <th v-for="cal in klassHeaderColumns" :key="cal.id">
                <div class="column-name">{{ cal.vName }}</div>
                <div class="column-hours">{{ cal.vTotalHours }}</div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td></td>
              <td
                v-for="calId in filteredKlassIds"
                :key="calId"
                class="td-create-bttn-container"
              >
                <button class="do-bttn td-bttn--small" @click="setCreateInCalendarId(calId)">
                  {{ i18n[locale]['create'] }}
                </button>
              </td>
            </tr>
            <tr v-if="!isActivities">
              <td></td>
              <td
                v-for="calId in filteredKlassIds"
                :key="calId"
                class="td-create-bttn-container"
              >
                <button class="do-bttn td-bttn--small" @click="openKlassRoomModal(calId)">
                  {{ i18n[locale]['edit'] }}
                </button>
              </td>
            </tr>

            <tr v-for="row in visibleSubjectsTable" :key="row.id">
              <th>
                <div class="th-wrench-icon-container">
                {{ row.name }}
                <span class="icon__bg-circle icon__bg-circle--small th-wrench-icon icon-wrench" @click.stop="openBulkColorModal(row)"></span>
                </div>
              </th>

              <template v-for="cell in row.cells">
                <td
                  v-if="cell.hasSubject"
                  :key="cell.calId"
                  class="td__subject"
                  @click="setEditSubject(cell.klass)"
                  :style="{'--subjectBgColor': cell.klass.color}"
                  :aria-hours="cell.vHours"
                  :content="cell.v_tooltip"
                  v-tippy="{
                    arrow: true,
                    onShow: () => !!cell.v_tooltip
                  }"
                  :class="[
                    {'td__subject--has-user': cell.v_subjectHasTeachers},
                    {'td__subject--has-room': cell.v_subjectHasRooms},
                    {'td__subject-has-other-klass': cell.v_subjectOtherKlasses && cell.v_subjectOtherKlasses.length > 0}
                  ]"
                >
                </td>

                <td
                  v-else
                  :key="cell.calId"
                  class="td__create-subject"
                  :content="cell.createTooltip"
                  v-tippy="{
                    animateFill: false,
                    animation : 'shift-away',
                    zIndex: '999999992',
                    boundary: 'window'
                  }"
                  @click.self="setIsCreateSubject(row.rawSubject, cell.klass)"
                >
                  <AddSubject
                    v-if="isCreateSubject(row.rawSubject, cell.klass)"
                    :subject="row.rawSubject"
                    :calendar="cell.klass"
                    :propHours="lastCreatedHoursBySubjectId[row.rawSubject.id]"
                    @createSubject="createExistingSubjectInCal"
                    @close="closeIsCreateSubject"
                  >
                  </AddSubject>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <modal v-if="isBulkColorModalOpen" @close="closeBulkColorModal">
      <template slot="body">
        <div class="bulk-color-modal__header" :style="{'backgroundColor': bulkColor || '#A0A5EF'}">
          <h2>{{ bulkColorRow ? bulkColorRow.name : '' }}</h2>
        </div>
        <div class="bulk-color-modal__body">
          <div class="bulk-color-modal__section">
            <div class="d-label-and-toggle" @click.stop="bulkNameEnabled = !bulkNameEnabled">
              <label class="d-label">{{ i18n[locale]['change_title'] || 'Skift titel' }}</label>
              <AppToggle :propOn="bulkNameEnabled" :allowChanges="false"></AppToggle>
            </div>
            <div v-if="bulkNameEnabled" class="bulk-color-modal__field">
              <input
                type="text"
                class="d-input"
                v-model="bulkName"
                :placeholder="bulkColorRow ? bulkColorRow.name : ''"
              >
            </div>
          </div>
          <div class="bulk-color-modal__section">
            <div class="d-label-and-toggle" @click.stop="bulkHoursEnabled = !bulkHoursEnabled">
              <label class="d-label">{{ i18n[locale]['change_hours'] || 'Opdater klokketimer i skoleåret' }}</label>
              <AppToggle :propOn="bulkHoursEnabled" :allowChanges="false"></AppToggle>
            </div>
            <div v-if="bulkHoursEnabled" class="bulk-color-modal__field">
              <input
                type="number"
                class="do-input"
                min="0"
                step="0.01"
                v-model.number="bulkHours"
                @focus="$event.target.select()"
                :placeholder="bulkColorRow && bulkColorRow.rawSubject ? String(bulkColorRow.rawSubject.hours || 0) : ''"
              >
            </div>
          </div>
          <div class="bulk-color-modal__section">
            <div class="d-label-and-toggle" @click.stop="bulkSubjectTypeEnabled = !bulkSubjectTypeEnabled">
              <label class="d-label">{{ i18n[locale]['change_subject_type'] || 'Skift briktype' }}</label>
              <AppToggle :propOn="bulkSubjectTypeEnabled" :allowChanges="false"></AppToggle>
            </div>
            <div v-if="bulkSubjectTypeEnabled" class="bulk-color-modal__field">
              <select v-model="bulkSubjectType" class="d-input__select">
                <option value="" disabled>--</option>
                <option v-for="t in bulkSubjectTypeOptions" :key="t.value" :value="t.value">{{ t.name }}</option>
              </select>
            </div>
          </div>
          <!--
          <div class="bulk-color-modal__section">
            <div class="d-label-and-toggle" @click.stop="bulkPreparationEnabled = !bulkPreparationEnabled">
              <label class="d-label">{{ i18n[locale]['change_preparation'] || 'Sæt forberedelsesfaktor' }}</label>
              <AppToggle :propOn="bulkPreparationEnabled" :allowChanges="false"></AppToggle>
            </div>
            <div v-if="bulkPreparationEnabled" class="bulk-color-modal__field">
              <input
                type="number"
                class="do-input"
                min="-1"
                step="0.01"
                v-model.number="bulkPreparation"
                @focus="$event.target.select()"
                :placeholder="bulkColorRow && bulkColorRow.rawSubject && bulkColorRow.rawSubject.preparation != null ? String(bulkColorRow.rawSubject.preparation) : ''"
              >
            </div>
          </div>
          -->

          <div class="bulk-color-modal__section">
            <div class="d-label-and-toggle" @click.stop="bulkColorEnabled = !bulkColorEnabled">
              <label class="d-label">{{ i18n[locale]['change_color'] || 'Skift farve' }}</label>
              <AppToggle :propOn="bulkColorEnabled" :allowChanges="false"></AppToggle>
            </div>
            <div v-if="bulkColorEnabled" class="bulk-color-modal__field">
              <ColorPicker
                :initialColor="bulkColor"
                @changedColor="setBulkColor"
              ></ColorPicker>
            </div>
          </div>
          <div class="bulk-color-modal__section">
            <div class="d-label-and-toggle" @click.stop="bulkRoomEnabled = !bulkRoomEnabled">
              <label class="d-label"><span class="icon-location"></span> {{ i18n[locale]['change_room'] || 'Skift lokale' }}</label>
              <AppToggle :propOn="bulkRoomEnabled" :allowChanges="false"></AppToggle>
            </div>
            <div v-if="bulkRoomEnabled" class="bulk-color-modal__field">
              <DSelectCalendarIds
                itemType="ROOM"
                :isMultiple="false"
                :preSelectedItems="[]"
                @update="setBulkRoom"
              ></DSelectCalendarIds>
            </div>
          </div>
          <div class="bulk-color-modal__actions">
            <button class="do-bttn" :disabled="bulkColorLoading" @click="closeBulkColorModal">{{ i18n[locale]['cancel'] || 'Annullér' }}</button>
            <button
              class="do-bttn do-bttn--submit"
              :class="{'do-bttn--submit--loading': bulkColorLoading}"
              :disabled="bulkColorLoading || (!bulkColorEnabled && !bulkRoomEnabled && !bulkNameEnabled && !bulkHoursEnabled && !bulkSubjectTypeEnabled && !bulkPreparationEnabled)"
              @click="saveBulkColor"
            >{{ i18n[locale]['update'] || 'Opdatér' }}</button>
          </div>
        </div>
      </template>
    </modal>

    <modal v-if="isKlassRoomModalOpen" @close="closeKlassRoomModal">
      <template slot="body">
        <div class="bulk-color-modal__header" :style="{'backgroundColor': '#468AD7'}">
          <h2>{{ klassRoomName }}</h2>
        </div>
        <div class="bulk-color-modal__body">
          <div class="bulk-color-modal__section">
            <label class="bulk-color-modal__label"><span class="icon-location"></span> {{ i18n[locale]['add_room_to_klass'] }}</label>
            <DSelectCalendarIds
              itemType="ROOM"
              :isMultiple="false"
              :preSelectedItems="klassRoomSelected"
              @update="setKlassRoom"
            ></DSelectCalendarIds>
          </div>
          <div class="bulk-color-modal__section" v-if="klassRoomSubjects.length > 0">
            <label class="bulk-color-modal__label">{{ i18n[locale]['select_subjects'] }}</label>
            <ul class="klass-room-subjects">
              <li
                v-for="subj in klassRoomSubjects"
                :key="subj.id"
                class="klass-room-subjects__item"
                @click="toggleKlassRoomSubject(subj.id)"
              >
                <input
                  type="checkbox"
                  :checked="!klassRoomExcluded[subj.id]"
                  @click.stop="toggleKlassRoomSubject(subj.id)"
                >
                <span
                  class="klass-room-subjects__color"
                  :style="{'backgroundColor': subj.cell.klass.color}"
                ></span>
                <span class="klass-room-subjects__name">{{ subj.name }}</span>
              </li>
            </ul>
          </div>
          <div class="bulk-color-modal__actions">
            <button class="do-bttn" :disabled="klassRoomLoading" @click="closeKlassRoomModal">{{ i18n[locale]['cancel'] }}</button>
            <button
              class="do-bttn do-bttn--submit"
              :class="{'do-bttn--loading': klassRoomLoading}"
              :disabled="klassRoomLoading || klassRoomSelected.length === 0"
              @click="saveKlassRoom"
            >{{ i18n[locale]['update'] }}</button>
          </div>
        </div>
      </template>
    </modal>

    <SubjectForm
      v-if="isSubjectModal"
      :propCalendarId="createInCalendarId"
      :showModal="isSubjectModal"
      :editSubject="editSubject"
      :isActivities="isActivities"
      @close="closeModal"
      @createSubject="createNewSubject"
      @updateSubject="updateSubject"
      @deleteSubject="deleteSubject"
      @saveAndShowTab="saveAndShowTab"
    >
    </SubjectForm>
  </div>
</template>

<script>
import Loader from '../common/Loader'
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import SubjectForm from '../Subjects/SubjectForm'
import AddSubject from './BulkCreateSubjectsSubject'
import eventbus from '../../eventbus'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import Banner from '../common/Banner.vue'
import modal from '../Subjects/Modal'
import ColorPicker from '../Subjects/ColorPicker'
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import AppToggle from '../common/AppToggle.vue'
import docendo from '../../docendo'

const I18N = {
  da: {
    headline: 'Fagoprettelse',
    create_subject: 'Opret fag',
    create_classes_first: 'Opret klasser i sidemenuen først',
    import_subjects: 'Importer fag',
    subjects: 'Fag',
    grades: 'Klasser',
    no_subjects: 'Ingen fag',
    create: 'Opret',
    in: 'i',
    change_title: 'Skift titel',
    change_hours: 'Opdater klokketimer i skoleåret',
    change_subject_type: 'Skift briktype',
    change_preparation: 'Sæt forberedelsesfaktor',
    change_color: 'Skift farve',
    change_room: 'Tilføj lokale',
    cancel: 'Annullér',
    update: 'Opdatér',
    add_room_to_klass: 'Tilføj lokale',
    select_subjects: 'Vælg fag',
    edit: 'Stamlokale',
    tooltip_teachers: 'Undervisere',
    tooltip_rooms: 'Lokaler',
    tooltip_klasses: 'Klasser'
  },
  en: {
    headline: 'Create course',
    create_subject: 'Create course',
    create_classes_first: 'Start by creating grades/groups',
    import_subjects: 'Import courses',
    subjects: 'Courses',
    grades: 'Grades/groups',
    no_subjects: 'No courses',
    create: 'Create',
    in: 'in',
    change_title: 'Change title',
    change_hours: 'Update clock hours for the school year',
    change_subject_type: 'Change tile type',
    change_preparation: 'Set preparation factor',
    change_color: 'Change color',
    change_room: 'Add room',
    cancel: 'Cancel',
    update: 'Update',
    add_room_to_klass: 'Add room',
    select_subjects: 'Select subjects',
    edit: 'Room',
    tooltip_teachers: 'Teachers',
    tooltip_rooms: 'Rooms',
    tooltip_klasses: 'Grades/groups'
  },
  nb: {
    headline: 'Fag- og timefordeling',
    create_subject: 'Opprett fag',
    create_classes_first: 'Opprett klasser i sidemenyen',
    import_subjects: 'Importer fag',
    grades: 'Klasser',
    no_subjects: 'Ingen fag',
    create: 'Opprett',
    in: 'i',
    change_title: 'Endre tittel',
    change_hours: 'Oppdater klokketimer i skoleåret',
    change_subject_type: 'Endre brikketype',
    change_preparation: 'Sett forberedelsesfaktor',
    change_color: 'Endre farge',
    change_room: 'Legg til lokale',
    cancel: 'Angre',
    update: 'Oppdater',
    add_room_to_klass: 'Legg til lokale',
    select_subjects: 'Velg fag',
    edit: 'Lokale',
    tooltip_teachers: 'Lærere',
    tooltip_rooms: 'Lokaler',
    tooltip_klasses: 'Klasser'
  }
}

export default {
  components: { DoFiltersFilter, Loader, AddSubject, SubjectForm, ChangeSchoolYear, Banner, modal, ColorPicker, DSelectCalendarIds, AppToggle },
  props: {
    isActivities: {
      default: false
    },
    propIsSubjectModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      tableWidth: '100%',
      editSubject: null,
      isSubjectModal: false,
      isTheCreateCalenderSubjectActive: null,
      isSubjectImportModal: false,
      createInCalendarId: null,
      _tableWidthRaf: null,
      isBulkColorModalOpen: false,
      bulkColorRow: null,
      bulkColor: '',
      bulkColorLoading: false,
      bulkColorEnabled: false,
      bulkName: '',
      bulkNameEnabled: false,
      bulkHours: null,
      bulkHoursEnabled: false,
      bulkSubjectType: '',
      bulkSubjectTypeEnabled: false,
      bulkCalSubjectTypes: [],
      bulkPreparation: null,
      bulkPreparationEnabled: false,
      bulkRoom: [],
      bulkRoomSelected: [],
      bulkRoomEnabled: false,
      isKlassRoomModalOpen: false,
      klassRoomCalId: null,
      klassRoomName: '',
      klassRoomSelected: [],
      klassRoomLoading: false,
      klassRoomExcluded: {},
      selectedKlassFilters: [],
      lastCreatedHoursBySubjectId: {}
    }
  },
  computed: {
    schoolIsFromDK () {
      return this.$store.getters.schoolCountry.toLowerCase() === 'dk'
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    noActiveSchoolYears () {
      return this.$store.state.noActiveSchoolYears
    },
    locale () {
      return this.$store.getters.locale
    },
    isLoading () {
      return this.$store.state.subjects.isLoading
    },
    i18n () {
      return I18N
    },
    subjectTypes () {
      return this.$store.state.subjects.subjectTypes[this.locale]
    },
    bulkSubjectTypeOptions () {
      const types = this.subjectTypes || []
      const allowed = this.bulkCalSubjectTypes
      let filtered = (allowed && allowed.length > 0)
        ? types.filter(t => allowed.indexOf(t.value) > -1)
        : types
      const current = this.bulkColorRow && this.bulkColorRow.rawSubject
        ? this.bulkColorRow.rawSubject.subject_type
        : null
      if (current && !filtered.find(t => t.value === current)) {
        const fallback = types.find(t => t.value === current)
        if (fallback) {
          filtered = filtered.concat([fallback])
        }
      }
      return filtered.slice().sort((a, b) => a.name.localeCompare(b.name, this.locale))
    },
    subjectTypeFilters () {
      const excluded = ['activity', 'other_tasks', 'other_stuff', 'club']

      return this.subjectTypes
        .filter(type => !excluded.includes(type.value))
        .map(type => type.name)
    },
    selectedSubjectTypeFilters () {
      return this.$store.state.subjects.selectedSubjectTypeFilters
    },

    subjectsInKlasses () {
      return this.$store.getters.uniqueSubjects ? this.$store.getters.uniqueSubjects : []
    },
    subjectsInKlassHash () {
      return this.$store.getters.subjectsInKlassHash
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },

    klassCalendarsSorted () {
      return this.$store.getters.klassCalendars
    },
    klassCalendarInHash () {
      return this.$store.getters.allKlassCalendarsInHash
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },

    klassIds () {
      return this.$store.getters.klassCalIdsHashSortedOnName || []
    },

    klassTags () {
      return (this.klassCalendarsSorted || []).map(calendar => calendar.name)
    },

    klassFilterStorageKey () {
      return this.isActivities
        ? 'doBulkCreateActivitiesKlassFilters'
        : 'doBulkCreateSubjectsKlassFilters'
    },

    filteredKlassIds () {
      if (!this.selectedKlassFilters || this.selectedKlassFilters.length === 0) {
        return this.klassIds
      }
      const hash = this.klassCalendarInHash || {}
      return this.klassIds.filter(calId => {
        const cal = hash[calId]
        return cal && this.selectedKlassFilters.indexOf(cal.name) > -1
      })
    },

    klassesWithoutSubjects () {
      return this.klassCalendarsSorted.filter(klass => !this.subjectsInKlassHash[klass.id])
    },

    translations () {
      const loc = this.locale || 'en'
      return this.i18n && this.i18n[loc] ? this.i18n[loc] : {}
    },

    schoolId () {
      return this.$store.getters.schoolId
    },

    klassHeaderColumns () {
      const ids = this.filteredKlassIds
      const totalHoursByCalId = this.totalHoursByCalIdFromRowCells

      return ids.map(calId => {
        const base = this.klassCalendarInHash && this.klassCalendarInHash[calId]
          ? this.klassCalendarInHash[calId]
          : { id: calId }

        return {
          ...base,
          vName: this.getCalendarName(calId),
          vTotalHours: totalHoursByCalId[calId] || '0'
        }
      })
    },
    totalHoursByCalIdFromRowCells () {
      const ids = this.filteredKlassIds
      const rows = this.subjectsTable || []

      const totals = {}

      ids.forEach(calId => {
        totals[calId] = 0
      })

      rows.forEach(row => {
        const cells = row.cells || []
        cells.forEach(cell => {
          if (!cell || !cell.hasSubject) {
            return
          }
          const hours = cell.vHours * 1
          if (!hours) {
            return
          }
          const key = cell.calId
          if (totals[key] === undefined) {
            totals[key] = 0
          }
          totals[key] += hours
        })
      })

      const out = {}
      ids.forEach(calId => {
        out[calId] = this.formatHours(totals[calId] || 0)
      })

      return out
    },
    totalHoursByCalId () {
      const ids = this.klassIds
      const subjectsInKlassHash = this.subjectsInKlassHash || {}
      const subjectsInHash = this.subjectsInHash || {}

      const hoursMap = {}

      ids.forEach(calId => {
        const subjectIds = subjectsInKlassHash[calId] || []
        let total = 0
        subjectIds.forEach(subjectId => {
          const subject = subjectsInHash[subjectId]
          if (!subject) {
            return
          }
          total += this.getKlassHours(subject, calId) * 1
        })

        hoursMap[calId] = total
      })

      return hoursMap
    },
    subjectsTable () {
      const klassIds = this.filteredKlassIds
      const translations = this.translations
      const createText = translations['create'] || 'Create'
      const inText = translations['in'] || 'in'

      const allCalendarsInHash = this.allCalendarsInHash || {}
      const klassCalendarInHash = this.klassCalendarInHash || {}

      const memo = {
        byKey: Object.create(null)
      }

      const getCalIdsKey = calIds => {
        if (!calIds || calIds.length < 1 || !calIds[0]) {
          return ''
        }
        return calIds.join(',')
      }

      const computeFlags = (subjectKlass, calIds) => {
        const key = getCalIdsKey(calIds)
        if (!key) {
          return {
            v_subjectHasTeachers: false,
            v_subjectHasRooms: false,
            v_subjectOtherKlasses: '',
            v_userNames: '',
            v_tooltip: ''
          }
        }

        if (memo.byKey[key]) {
          return memo.byKey[key]
        }

        let hasTeachers = false
        let hasRooms = false
        const otherKlasses = []
        const userNames = []
        const roomNames = []

        calIds.forEach(id => {
          const cal = allCalendarsInHash[id]
          if (!cal) {
            return
          }
          if (cal.type === 'user') {
            hasTeachers = true
            if (cal.name) {
              userNames.push(cal.name)
            }
          }
          if (cal.type === 'room') {
            hasRooms = true
            if (cal.name) {
              roomNames.push(cal.name)
            }
          }
          if (cal.type === 'klass' && id !== subjectKlass.calendar_id) {
            if (cal.name) {
              otherKlasses.push(cal.name)
            } else {
              otherKlasses.push('')
            }
          }
        })

        const tooltipLines = [
          [translations['tooltip_teachers'], userNames],
          [translations['tooltip_rooms'], roomNames],
          [translations['tooltip_klasses'], otherKlasses.filter(Boolean)]
        ]
          .filter(line => line[1].length > 0)
          .map(([label, names]) => '<b>' + label + ':</b> ' + names.map(this.escapeHtml).join(', '))

        const res = {
          v_subjectHasTeachers: hasTeachers,
          v_subjectHasRooms: hasRooms,
          v_subjectOtherKlasses: otherKlasses,
          v_userNames: userNames,
          v_tooltip: tooltipLines.join('<br>')
        }

        memo.byKey[key] = res
        return res
      }

      return this.subjectsInKlasses.map(subject => {
        const klasses = subject.klasses || {}

        const cells = klassIds.map(calId => {
          const klass = klasses[calId] ? klasses[calId] : { calendar_id: parseInt(calId) }
          const hasSubject = !!(klasses[calId] && klasses[calId].hours > -1)

          if (hasSubject) {
            const calIds = klass.calendar_ids
            const flags = computeFlags(klass, calIds)

            return {
              calId,
              klass,
              hasSubject: true,
              vHours: this.getKlassHours(subject, calId),
              v_subjectHasTeachers: flags.v_subjectHasTeachers,
              v_subjectHasRooms: flags.v_subjectHasRooms,
              v_subjectOtherKlasses: flags.v_subjectOtherKlasses,
              v_userNames: flags.v_userNames,
              v_tooltip: flags.v_tooltip
            }
          }

          const subjectName = subject && subject.name ? subject.name : ''
          const calName = klassCalendarInHash[calId] && klassCalendarInHash[calId].name
            ? klassCalendarInHash[calId].name
            : 'id: ' + calId

          return {
            calId,
            klass,
            hasSubject: false,
            createTooltip: createText + ' ' + subjectName + ' ' + inText + ' ' + calName
          }
        })

        let resolvedColor = subject.color
        for (const calId in klasses) {
          if (klasses[calId] && klasses[calId].color) {
            resolvedColor = klasses[calId].color
            break
          }
        }

        const resolvedSubject = resolvedColor !== subject.color
          ? Object.assign({}, subject, { color: resolvedColor })
          : subject

        return {
          id: subject.id,
          name: subject.name,
          rawSubject: resolvedSubject,
          cells
        }
      })
    },
    visibleSubjectsTable () {
      return (this.subjectsTable || []).filter(row => {
        const t = row.rawSubject && row.rawSubject.subject_type
        if (this.isActivities) {
          return t === 'activity'
        }
        return !row.rawSubject || t !== 'activity'
      })
    },
    klassRoomSubjects () {
      if (!this.klassRoomCalId) return []
      return (this.subjectsTable || [])
        .map(row => {
          const cell = row.cells.find(c => c.calId === this.klassRoomCalId)
          if (!cell || !cell.hasSubject) return null
          return { id: cell.klass.id, name: row.name, cell: cell }
        })
        .filter(Boolean)
    }
  },

  watch: {
    activeSchoolYear () {
      this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
    },
    propIsSubjectModal (bool) {
      this.isSubjectModal = bool
    },
    klassIds () {
      this.scheduleSetTableWidth()
    },
    subjectsInKlasses () {
      this.scheduleSetTableWidth()
    },
    selectedKlassFilters () {
      this.scheduleSetTableWidth()
    }
  },

  directives: {
    focus: {
      inserted: function (el) {
        el.focus()
      }
    }
  },

  methods: {
    escapeHtml (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    formatHours (value) {
      const num = value * 1
      if (Math.floor(num) === num) {
        return '' + num
      }
      return num.toFixed(2).replace(/\.?0+$/, '')
    },
    onNewSubjectsCreated () {
      this.readSubject()
    },

    scheduleSetTableWidth () {
      if (this._tableWidthRaf) {
        window.cancelAnimationFrame(this._tableWidthRaf)
      }
      this._tableWidthRaf = window.requestAnimationFrame(() => {
        this._tableWidthRaf = null
        this.setTableWidth()
      })
    },

    getKlassHours (subject, calId) {
      if (!subject.klasses || !subject.klasses[calId]) {
        return subject.hours || 0
      }

      const klassSubject = subject.klasses[calId]
      const calendars = klassSubject.subject_calendars || []
      const targetId = parseInt(calId)

      for (let i = 0; i < calendars.length; i++) {
        const sc = calendars[i]
        if (sc && sc.calendar_id === targetId) {
          if (sc.hours) {
            return sc.hours
          }
          break
        }
      }

      return klassSubject.hours || 0
    },

    getCalendarName (calId) {
      return this.klassCalendarInHash && this.klassCalendarInHash[calId]
        ? this.klassCalendarInHash[calId].name
        : 'id: ' + calId
    },

    setCreateInCalendarId (calId) {
      this.createInCalendarId = calId
      if (calId) {
        this.isSubjectModal = true
      } else {
        this.isSubjectModal = false
      }
    },

    toggleSelectedSubjectTypeFilters (filter) {
      this.$store.commit('TOGGLE_SUBJECT_TYPE_FILTER', filter)
    },

    clearSelectedSubjectTypeFilters () {
      this.$store.commit('CLEAR_SUBJECT_TYPE_FILTERS')
    },

    toggleKlassTag (tag) {
      const next = this.selectedKlassFilters.slice()
      const idx = next.indexOf(tag)
      if (idx > -1) {
        next.splice(idx, 1)
      } else {
        next.push(tag)
      }
      this.selectedKlassFilters = next
      this.$helpers.setLocalStorage(this.klassFilterStorageKey, JSON.stringify(next))
    },

    clearSelectedKlassFilters () {
      this.selectedKlassFilters = []
      this.$helpers.setLocalStorage(this.klassFilterStorageKey, JSON.stringify([]))
    },

    loadSelectedKlassFiltersFromLocalStorage () {
      const stored = this.$helpers.getLocalStorage(this.klassFilterStorageKey)
      if (!stored) {
        return
      }
      let parsed = []
      try {
        parsed = JSON.parse(stored)
      } catch (e) {
        parsed = []
      }
      if (!Array.isArray(parsed)) {
        return
      }
      this.selectedKlassFilters = parsed
    },

    setImportSubjects () {
      eventbus.theKlassesToImportSubjectTo = [...this.klassesWithoutSubjects]
      eventbus.createKlassModal = true
    },

    setIsCreateSubject (subject, calendarSubject) {
      setTimeout(_ => {
        this.isTheCreateCalenderSubjectActive = subject.id + '_' + calendarSubject.calendar_id
      }, 0)
    },

    isCreateSubject (subject, calendarSubject) {
      return this.isTheCreateCalenderSubjectActive === subject.id + '_' + calendarSubject.calendar_id
    },

    closeIsCreateSubject () {
      this.isTheCreateCalenderSubjectActive = null
    },

    closeModal () {
      this.editSubject = null
      this.isSubjectModal = false
      this.createInCalendarId = null
      this.$emit('closeSubjectModal')
    },

    setEditSubject (editSubject) {
      this.editSubject = Object.assign({}, editSubject)
      this.isSubjectModal = true
      setTimeout(_ => {
        this.isTheCreateCalenderSubjectActive = null
      }, 1)
    },

    createNewSubject (newSubject) {
      newSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('createSubject', newSubject)
      this.closeModal()
    },

    createExistingSubjectInCal (brik) {
      const numericHours = brik.hours * 1
      if (brik.subject && brik.subject.id != null && !isNaN(numericHours) && numericHours > 0) {
        this.$set(this.lastCreatedHoursBySubjectId, brik.subject.id, numericHours)
      }
      let newSubject = {
        calendarId: brik.calendar.calendar_id,
        name: brik.subject.name,
        color: brik.subject.color,
        defaultDuration: brik.subject.default_duration,
        preparation: brik.subject.preparation != null && brik.subject.preparation !== '' ? brik.subject.preparation : null,
        hours: brik.hours,
        subjectType: brik.subject.subject_type,
        calendarIds: [],
        subjectCode: brik.subject.subject_code
      }
      newSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('createSubject', newSubject)
    },

    updateSubject (updatedSubject) {
      updatedSubject['schoolId'] = updatedSubject.schoolId ? updatedSubject.schoolId : window.SCHOOL.id
      updatedSubject['schoolYearId'] = updatedSubject.schoolYearId ? updatedSubject.schoolYearId : this.activeSchoolYear.id
      this.$store.dispatch('updateSubject', updatedSubject)
      this.closeModal()
    },
    saveAndShowTab (updatedSubject, tab) {
      updatedSubject['schoolId'] = updatedSubject.schoolId ? updatedSubject.schoolId : window.SCHOOL.id
      updatedSubject['schoolYearId'] = updatedSubject.schoolYearId ? updatedSubject.schoolYearId : this.activeSchoolYear.id
      this.updateSubject(updatedSubject)
      this._pendingTab = tab
      this._pendingSubjectId = updatedSubject.id
      setTimeout(() => {
        this.isSubjectModal = true
        docendo.showSubject(this._pendingSubjectId, success => {
          const pendingTab = this._pendingTab
          this._pendingTab = null
          this._pendingSubjectId = null
          this.editSubject = Object.assign({}, success, { _openTab: pendingTab })
        })
      }, 500)
    },

    deleteSubject (editSubject) {
      let deletedSubject = {
        id: editSubject.id,
        school_id: editSubject.school_id,
        school_year_id: this.activeSchoolYear.id
      }
      deletedSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('deleteSubject', deletedSubject)
      this.closeModal()
    },

    setTableWidth () {
      const el = this.$refs.tableHead
      if (!el) {
        return
      }
      const tableBCR = el.getBoundingClientRect()
      this.tableWidth = tableBCR.width + 15 + 'px'
    },

    readSubject () {
      this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
    },

    loadBulkCalSubjectTypes () {
      const country = this.$store.getters.schoolCountry
      docendo.subjectsForCalendarType('klass', country, data => {
        const list = typeof data === 'string' ? JSON.parse(data) : data
        this.bulkCalSubjectTypes = Array.isArray(list) ? list : []
      }, () => {
        this.bulkCalSubjectTypes = []
      })
    },

    openBulkColorModal (row) {
      this.bulkColorRow = row
      this.bulkColor = row.rawSubject.color || '#A0A5EF'
      this.bulkName = row.name || ''
      this.bulkHours = row.rawSubject && row.rawSubject.hours != null ? row.rawSubject.hours : null
      this.bulkSubjectType = row.rawSubject && row.rawSubject.subject_type ? row.rawSubject.subject_type : ''
      this.bulkPreparation = row.rawSubject && row.rawSubject.preparation != null ? row.rawSubject.preparation * 1 : null
      const roomIds = this.getRoomIdsFromRow(row)
      this.bulkRoom = roomIds
      this.bulkRoomSelected = [].concat(roomIds)
      this.bulkColorEnabled = false
      this.bulkNameEnabled = false
      this.bulkHoursEnabled = false
      this.bulkSubjectTypeEnabled = false
      this.bulkPreparationEnabled = false
      this.bulkRoomEnabled = false
      this.isBulkColorModalOpen = true
    },

    getRoomIdsFromRow (row) {
      const allCals = this.allCalendarsInHash || {}
      const cells = row.cells || []
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i]
        if (!cell.hasSubject) {
          continue
        }
        const calIds = cell.klass.calendar_ids || []
        const roomIds = calIds.filter(id => allCals[id] && allCals[id].type === 'room')
        if (roomIds.length > 0) {
          return roomIds
        }
      }
      return []
    },

    setBulkColor (color) {
      this.bulkColor = color
    },

    setBulkRoom (rooms) {
      this.bulkRoomSelected = rooms.map(r => r.id)
    },

    closeBulkColorModal () {
      if (this.bulkColorLoading) {
        return
      }
      this.isBulkColorModalOpen = false
      this.bulkColorRow = null
      this.bulkColor = ''
      this.bulkName = ''
      this.bulkHours = null
      this.bulkSubjectType = ''
      this.bulkPreparation = null
      this.bulkRoom = []
      this.bulkRoomSelected = []
      this.bulkColorEnabled = false
      this.bulkNameEnabled = false
      this.bulkHoursEnabled = false
      this.bulkSubjectTypeEnabled = false
      this.bulkPreparationEnabled = false
      this.bulkRoomEnabled = false
    },

    saveBulkColor () {
      if (!this.bulkColorRow || this.bulkColorLoading) {
        return
      }
      if (!this.bulkColorEnabled && !this.bulkRoomEnabled && !this.bulkNameEnabled && !this.bulkHoursEnabled && !this.bulkSubjectTypeEnabled && !this.bulkPreparationEnabled) {
        return
      }
      const cellsToUpdate = (this.bulkColorRow.cells || []).filter(cell => cell.hasSubject)
      if (cellsToUpdate.length === 0) {
        this.closeBulkColorModal()
        return
      }
      const ids = cellsToUpdate.map(cell => cell.klass.id)
      const changes = {}
      if (this.bulkNameEnabled && this.bulkName.trim().length > 0) {
        changes.name = this.bulkName.trim()
      }
      if (this.bulkHoursEnabled && typeof this.bulkHours === 'number' && !isNaN(this.bulkHours) && this.bulkHours >= 0) {
        changes.hours = this.bulkHours
      }
      if (this.bulkSubjectTypeEnabled && this.bulkSubjectType) {
        changes.subject_type = this.bulkSubjectType
      }
      if (this.bulkPreparationEnabled && typeof this.bulkPreparation === 'number' && !isNaN(this.bulkPreparation) && this.bulkPreparation >= -1) {
        changes.preparation = this.bulkPreparation
      }
      if (this.bulkColorEnabled) {
        changes.color = this.bulkColor
      }
      if (this.bulkRoomEnabled && this.bulkRoomSelected.length > 0) {
        changes.add_calendar_id = this.bulkRoomSelected[0]
      }
      this.bulkColorLoading = true
      docendo.bulkUpdateSubjects(this.schoolId, ids, changes, data => {
        const subjects = typeof data === 'string' ? JSON.parse(data) : data
        subjects.forEach(subj => {
          subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : []
          this.$store.commit('updateSubject', subj)
        })
        this.bulkColorLoading = false
        this.closeBulkColorModal()
      }, () => {
        this.bulkColorLoading = false
        this.closeBulkColorModal()
      })
    },
    openKlassRoomModal (calId) {
      this.klassRoomCalId = calId
      const cal = this.klassHeaderColumns.find(c => c.id === calId)
      this.klassRoomName = cal ? cal.vName : ''
      this.klassRoomSelected = []
      this.klassRoomExcluded = {}
      this.klassRoomLoading = false
      this.isKlassRoomModalOpen = true
    },
    setKlassRoom (rooms) {
      this.klassRoomSelected = rooms
    },
    toggleKlassRoomSubject (subjectId) {
      const copy = Object.assign({}, this.klassRoomExcluded)
      if (copy[subjectId]) {
        delete copy[subjectId]
      } else {
        copy[subjectId] = true
      }
      this.klassRoomExcluded = copy
    },
    closeKlassRoomModal () {
      if (this.klassRoomLoading) return
      this.isKlassRoomModalOpen = false
      this.klassRoomCalId = null
      this.klassRoomName = ''
      this.klassRoomSelected = []
      this.klassRoomExcluded = {}
      this.klassRoomLoading = false
    },
    saveKlassRoom () {
      if (this.klassRoomSelected.length === 0) return
      const subjects = this.klassRoomSubjects.filter(s => !this.klassRoomExcluded[s.id])
      if (subjects.length === 0) {
        this.closeKlassRoomModal()
        return
      }
      const ids = subjects.map(s => s.id)
      const firstSelected = this.klassRoomSelected[0]
      const calendarId = firstSelected && typeof firstSelected === 'object' ? firstSelected.id : firstSelected
      if (!calendarId) return
      const changes = { add_calendar_id: calendarId }
      this.klassRoomLoading = true
      docendo.bulkUpdateSubjects(this.schoolId, ids, changes, data => {
        const updated = typeof data === 'string' ? JSON.parse(data) : data
        updated.forEach(subj => {
          subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : []
          this.$store.commit('updateSubject', subj)
        })
        this.klassRoomLoading = false
        this.closeKlassRoomModal()
      }, () => {
        this.klassRoomLoading = false
        this.closeKlassRoomModal()
      })
    }
  },

  mounted () {
    window.addEventListener('new_subjects_created', this.onNewSubjectsCreated)
    this.scheduleSetTableWidth()
    this.loadBulkCalSubjectTypes()
    this.loadSelectedKlassFiltersFromLocalStorage()
  },

  beforeDestroy () {
    window.removeEventListener('new_subjects_created', this.onNewSubjectsCreated)
    if (this._tableWidthRaf) {
      window.cancelAnimationFrame(this._tableWidthRaf)
      this._tableWidthRaf = null
    }
  }
}
</script>


<style scoped>
@import '../../assets/css/variables.css';

.subjects {
  min-height: 400px;
}

.subjects--is-activities {
  display: flex;
  flex-direction: column;
}

.subjects-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.subjects-header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.subjects-header__left > h1 {
  margin-right: 20px;
}

.subjects-header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.subjects-table {
  max-width: 100%;
  max-height: calc(100vh - (50px + 55px + 55px + 20px + 20px));
  overflow: scroll;
  position: relative;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
}

.d-table {
  position: relative;
  max-width: 100%;
  color: #3d4340;
  border-collapse: collapse;
}

.d-table thead th {
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  z-index: 1;
  top: 0;
  background: #f6f9fc;
  border-bottom: 1px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: .075rem;
  font-size: 1rem;
  border-collapse: collapse;
}

.d-table thead tr th:first-child, tbody tr th:first-child {
  left: 0;
  z-index: 1;
  width: 120px;
  text-align: left;
  padding: 0 8px;
}

.d-table tbody th {
  background-color: #ffffff;
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  z-index: 2;
  left: 0;
  border-collapse: collapse;
}

.column-name {
  max-width: 130px;
  min-width: 110px;
}

.d-table tr td, .d-table tr th {
  height: 40px;
}

.d-table thead tr th { 
  height: 50px;
  border-left: 1px solid #eee;
}
.d-table tr td, .d-table tr th {
  transition: background-color 0.15s;
}

.d-table tbody tr:hover td, .d-table tbody tr:hover th {
  background-color: aliceblue;
}

.td__subject {
  position: relative;
}

.td__subject:before {
  content: attr(aria-hours);
  position: absolute;
  top: 4px;
  left: 2px;
  right: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: var(--subjectBgColor);
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  opacity: 0.9;
  text-align: left;
}

.td__subject--has-room:after {
  content: "\ea38";
  position: absolute;
  top: 12px;
  right: 8px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.td__subject-has-other-klass:after {
  content: "\EA62";
  position: absolute;
  top: 12px;
  right: 8px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.td__subject--has-user:after {
  content: "\eaf7";
  position: absolute;
  top: 12px;
  right: 8px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.td__subject--has-room.td__subject--has-user:not(.td__subject--has-other-klass):after {
  content: '';
  width: 18px;
  top: 14px;
  height: 18px;
  background-image: url('https://app.docendo.dk/static/img/app/bulk_create_subjects_userlocation.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__subject-has-other-klass.td__subject--has-user:not(.td__subject--has-room):after {
  content: '';
  width: 22px;
  top: 14px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_userklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__subject-has-other-klass.td__subject--has-room:not(.td__subject--has-user):after {
  content: '';
  width: 18px;
  top: 14px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_locationklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__subject-has-other-klass.td__subject--has-room.td__subject--has-user:after {
  content: '';
  width: 29px;
  top: 14px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_userlocationklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__create-subject {
  position: relative;
  cursor: pointer;
}

.td__create-subject:before {
  content: attr(aria-hours);
  position: absolute;
  top: 4px;
  left: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 28px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #eeeeee;
  overflow: hidden;
  opacity: 0.9;
  text-align: left;
  transition: box-shadow 0.25s, border-color 0.25s;
  border: 1px solid transparent;
}

.td__create-subject:hover:before {
  content: '';
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  border-color: #468AD7;
}

.td__create-subject:after {
  content: '+';
  position: absolute;
  top: 8px;
  right: 0;
  left: 0;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dark-blue);
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background-color: #c0c0c0;
}

.subject {
  position: absolute;
  z-index: 12;
  top: 4px;
  left: 2px;
  right: 2px;
  bottom: 0;
  padding: 4px 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  text-align: left;
}

.text-explainer {
  font-size: 20px;
  font-style: italic;
}

.list-enter-active, .list-leave-active {
  transition: opacity 0.45s, transform 0.45s;
}

.list-enter, .list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.bttn.create-btn--secondary {
  height: 38px;
  padding: 2px 16px;
}

.do-input-and-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
}

.do-input-and-button > input {
  min-width: 220px;
  max-width: 220px;
  max-height: 28px;
}

.do-input-and-button > .icon__bg-circle {
  min-width: 30px;
}

.td-bttn--small {
  width: calc(100% - 1rem);
}

.td-create-bttn-container {
  text-align: center;
}

.column-hours {
  font-size: 12px;
  color: #737373;
  border-top: 1px solid #eee;
}

.th-wrench-icon {
  cursor: pointer;
  opacity: 0.35;
  transition: opacity 0.2s;
  font-size: 0.85rem;
  margin-right: 4px;
}

.th-wrench-icon:hover {
  opacity: 1;
}

.bulk-color-modal__header {
  padding: 24px 20px;
  border-radius: 4px 4px 0 0;
  transition: background-color 0.4s;
}

.bulk-color-modal__header > h2 {
  color: #ffffff;
  margin: 0;
}

.bulk-color-modal__body {
  padding: 1.5rem 2rem;
  max-height: calc(80vh - 80px);
  overflow-y: auto;
}

.bulk-color-modal__section {
  margin-bottom: 1.25rem;
}

.bulk-color-modal__section .d-label-and-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 0.75rem;
  border-radius: 4px;
  cursor: pointer;
}

.bulk-color-modal__section .d-label-and-toggle label {
  cursor: pointer;
}

.bulk-color-modal__section .d-label-and-toggle:hover {
  background: aliceblue;
}

.bulk-color-modal__field {
  margin-top: 0.75rem;
}

.bulk-color-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.th-wrench-icon-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.klass-room-subjects {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #e2e6ea;
  border-radius: 6px;
  overflow: hidden;
}

.klass-room-subjects__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #e2e6ea;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.klass-room-subjects__item:last-child {
  border-bottom: none;
}

.klass-room-subjects__item:hover {
  background-color: #f0f4f8;
}

.klass-room-subjects__item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: #468AD7;
}

.klass-room-subjects__color {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.klass-room-subjects__name {
  font-size: 0.925rem;
  color: #333;
}

</style>



// WEBPACK FOOTER //
// BulkCreateSubjects.vue?18cd4b27